import {
    EnvelopeIcon,
    MapPinIcon,
    PhoneIcon,
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useSmoothScroll } from '../hooks/useSmoothScroll';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { scrollToSection } = useSmoothScroll();
  const { t } = useTranslation();

  const footerLinks = {
    produto: [
      { name: t('footer.links.avocadoHass'), href: 'about-hass' },
      { name: t('footer.links.ripenessGuide'), href: 'ripeness-guide' },
      { name: t('footer.links.comparison'), href: 'comparison' },
      { name: t('footer.links.harvestWindow'), href: 'harvest' },
    ],
    empresa: [
      { name: t('footer.links.aboutCompany'), href: 'about-company' },
      { name: t('footer.links.whyChoose'), href: 'why-choose' },
    ],
    mercado: [
      { name: t('footer.links.marketOpportunity'), href: 'market-opportunity' },
    ],
    contato: [
      { name: t('footer.links.contact'), href: 'contact' },
      { name: t('footer.links.location'), href: 'contact' },
    ],
  };

  return (
    <footer className='bg-gradient-to-br from-avoprime-primary to-avoprime-secondary text-white'>
      <div className='container mx-auto px-4 sm:px-6 lg:px-8 py-24'>
        <div className='grid grid-cols-1 py-8 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12'>
          {/* Company Info */}
          <div className='lg:col-span-2'>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className='text-2xl font-eras font-bold mb-6'>Avoprime</h3>
              {/* Contact Info */}
              <div className='space-y-3'>
                <div className='flex items-center space-x-3'>
                  <MapPinIcon className='w-5 h-5 text-avoprime-light' />
                  <span className='text-avoprime-light/90'>
                    {t('footer.location')}
                  </span>
                </div>
                <div className='flex items-center space-x-3'>
                  <PhoneIcon className='w-5 h-5 text-avoprime-light' />
                  <span className='text-avoprime-light/90'>
                    {t('footer.phones')}
                  </span>
                </div>
                <div className='flex items-center space-x-3'>
                  <EnvelopeIcon className='w-5 h-5 text-avoprime-light' />
                  <span className='text-avoprime-light/90'>
                    {t('footer.email')}
                  </span>
                </div>
                
                {/* Social Media */}
                <div className='flex items-center space-x-3 mt-4'>
                  <span className='text-avoprime-light/90 text-sm font-medium'>
                    {t('contact.contactInfo.socialMedia.title')}:
                  </span>
                  <div className='flex space-x-2'>
                    <a
                      href='https://www.linkedin.com/company/avoprime/'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='w-8 h-8 bg-white/20 rounded-full flex items-center justify-center transition-all duration-300 hover:bg-white/30 hover:scale-110'
                    >
                      <svg className='w-4 h-4 text-white' fill='currentColor' viewBox='0 0 24 24'>
                        <path d='M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z'/>
                      </svg>
                    </a>
                    <a
                      href='https://www.instagram.com/avoprimebr/?utm_source=ig_web_button_share_sheet'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='w-8 h-8 bg-white/20 rounded-full flex items-center justify-center transition-all duration-300 hover:bg-white/30 hover:scale-110'
                    >
                      <svg className='w-4 h-4 text-white' fill='currentColor' viewBox='0 0 24 24'>
                        <path d='M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z'/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Footer Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h4 className='text-lg font-eras font-bold mb-4'>{t('footer.links.product')}	</h4>
            <ul className='space-y-2'>
              {footerLinks.produto.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className='text-avoprime-light/80 hover:text-white transition-colors duration-200 text-sm'
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h4 className='text-lg font-eras font-bold mb-4'>{t('footer.links.aboutUs')}</h4>
            <ul className='space-y-2'>
              {footerLinks.empresa.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className='text-avoprime-light/80 hover:text-white transition-colors duration-200 text-sm'
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h4 className='text-lg font-eras font-bold mb-4'>{t('footer.links.market')}</h4>
            <ul className='space-y-2'>
              {footerLinks.mercado.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className='text-avoprime-light/80 hover:text-white transition-colors duration-200 text-sm'
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className='border-t border-white/20 pt-8 pb-5'
        >
          <div className='flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0'>
            <div className='text-center md:text-left'>
              <p className='text-avoprime-light/80 text-sm'>
                {t('footer.copyright', { year: currentYear })}
              </p>
            </div>
            <div className='flex items-center space-x-6'>
              <div className='text-center'>
                <div className='text-avoprime-light/80 text-xs mb-1'>
                  {t('footer.links.location')}
                </div>
                <div className='text-avoprime-light text-sm font-medium'>
                  {t('footer.location')}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
